import React, { useMemo, useState, useEffect, useRef } from 'react';
import { VehicleDetails } from './finance/VehicleDetails';
import { FinancialInformation } from './finance/FinancialInformation';
import { Communications } from './finance/Communications';
import { Check, Pencil, Trash2, ArrowLeft, FileSignature, BookMarked, Copy, KeyRound, X } from 'lucide-react';
import { MigratePanel } from '../components/migration/MigratePanel';
import { readBooksOpening } from '../lib/booksOpeningStore';
import { adminEntryDateOnly, adminEntryIso, defaultAdminEntryDate, resolveWorkspaceAsOfDate } from '../lib/workspaceAsOf';
import { toast } from 'react-toastify';
import { appendDevLog, loadDevJson, removeDevKey, saveDevJson } from '../lib/devStorage';
import {
  deleteFinanceRecord,
  isLoanRecordPending,
  pendingLoanRecordIds,
  readFinanceRecords,
  serializeFinanceForm,
  sortByEntryDateDesc,
  formatWorkspaceEntryDate,
  upsertFinanceRecord,
  useFinanceRecords,
  type FinanceRecord,
} from '../lib/workspaceStore';
import { useTranslation } from '../lib/i18n';
import { PageSearchBar } from '../components/common/PageSearchBar';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, inRange, formatRangeLabel } from '../lib/dateFilter';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { exportLoanApplicationPdf } from '../lib/loanApplicationPdf';
import { provisionCustomerForFinance, type ProvisionResult } from '../lib/customerPortal';
import { useScrollToTop } from '../lib/useScrollToTop';
import { useCanWrite } from '../lib/permissions';
import { validatePaymentVia } from '../components/common/PaymentViaFields';
import { readFinanceDefaults } from '../lib/financeCatalogs';
import { consumeRefinanceQueue, refinanceFormFromClosed, prefillFromMainLedgerRecord, findFinanceRecordByHa, HA_PREFILL_FIELD_KEYS } from '../lib/financeRefinance';
import { withApplicantPhoto, cacheFinanceApplicantPhoto, findTenantFinanceRecordByHa } from '../lib/financePhotoCache';
import { financeApi, hasAuthToken } from '../api';
import { mediaSrc } from '../lib/mediaUrl';

type Step = 'vehicle' | 'financial' | 'communications';

const flowSteps: { key: Step; labelKey: string }[] = [
  { key: 'vehicle', labelKey: 'finance.stepVehicle' },
  { key: 'financial', labelKey: 'finance.stepFinancial' },
  { key: 'communications', labelKey: 'finance.stepParties' },
];

const FINANCE_DRAFT_KEY = 'finance_draft_v1';
const FINANCE_LOG_KEY = 'finance_submit_log';

function emptyFinanceForm() {
  return {
    haNumber: '',
    regNo: '',
    makers: '',
    model: '',
    chasisNo: '',
    engNo: '',
    isCB: false,
    isCLR: false,
    hpnDate: '',
    amount: '',
    agreement: '',
    agreementDate: adminEntryDateOnly(),
    agreementFinance: '',
    bankTransaction: '',
    bank: '',
    disbursementVia: '' as '' | 'cash' | 'online' | 'split',
    disbursementBankId: '',
    disbursementBankLabel: '',
    disbursementPaidBy: '',
    disbursementCashAmount: '',
    disbursementBankAmount: '',
    chqNoDescription: '',
    totalAmount: '',
    outPayment: '',
    opInterest: '',
    amountBalance: '',
    emiPeriod: '',
    checkEmiAmount: '',
    interestPercentage: '',
    totalInterest: '',
    emiAmount: '',
    customerName: '',
    mobileNumber: '',
    alternateMobile: '',
    guarantorName: '',
    guarantorMobile: '',
    refererName: '',
    refererNumber: '',
    customerAadhar: '',
    villageTown: '',
    line: '',
    customerAddress1: '',
    customerAddress2: '',
    guarantorAddress: '',
    recordDetails: '',
    applicantPhoto: '',
    documents: null as FileList | null,
  };
}

function validateFinanceWorkspaceMinimum(formData: ReturnType<typeof emptyFinanceForm>): string | null {
  if (!formData.regNo.trim() && !formData.haNumber.trim()) {
    return 'Enter registration number or HA number to identify the vehicle.';
  }
  if (!formData.customerName.trim()) return 'Customer name is required.';
  if (!formData.mobileNumber.trim()) return 'Customer mobile is required.';
  if (!/^\d{10}$/.test(formData.mobileNumber.replace(/\D/g, ''))) {
    return 'Customer mobile must be exactly 10 digits.';
  }
  if (formData.customerAadhar?.trim() && !/^\d{12}$/.test(String(formData.customerAadhar).replace(/\D/g, ''))) {
    return 'Customer Aadhaar must be exactly 12 digits.';
  }
  const principal = String(formData.totalAmount || formData.amount || '').replace(/,/g, '').trim();
  if (!principal) return 'Principal (total amount or amount) is required.';
  if (!formData.emiPeriod.trim()) return 'EMI period (months) is required.';
  if (!String(formData.emiAmount || '').replace(/,/g, '').trim()) return 'EMI amount is required.';
  const months = parseInt(formData.emiPeriod, 10);
  if (!Number.isFinite(months) || months <= 0) return 'EMI period must be a positive number of months.';
  const principalNum = Math.max(0, parseFloat(principal) || 0);
  const payErr = validatePaymentVia(
    {
      paymentVia: formData.disbursementVia || '',
      companyBankId: formData.disbursementBankId || '',
      paidBy: formData.disbursementPaidBy || '',
      cashAmount: formData.disbursementCashAmount || '',
      bankAmount: formData.disbursementBankAmount || '',
    },
    'disbursement',
    principalNum
  );
  if (payErr) return payErr;
  return null;
}

function snapshotToForm(data: Record<string, unknown>) {
  const base = emptyFinanceForm();
  (Object.keys(base) as (keyof typeof base)[]).forEach((k) => {
    if (k === 'documents') return;
    if (k in data && data[k] !== undefined) {
      (base as Record<string, unknown>)[k] = data[k] as never;
    }
  });
  base.documents = null;
  return base;
}

export function Finance({ setActiveItem }: { setActiveItem: (item: string) => void }) {
  const [pageMode, setPageMode] = useState<'list' | 'wizard' | 'migrate'>('list');
  const [currentStep, setCurrentStep] = useState<Step>('vehicle');
  const [formData, setFormData] = useState(emptyFinanceForm);
  const [editingFinanceId, setEditingFinanceId] = useState<string | null>(null);
  /** Allocated when opening a new/refinance wizard so photos upload as customer_<id> before save. */
  const [draftClientId, setDraftClientId] = useState<string | null>(null);
  const [isRefinance, setIsRefinance] = useState(false);
  const customerPhotoId = editingFinanceId ?? draftClientId;
  const draftRestored = useRef(false);
  const listAnchorRef = useRef<HTMLElement>(null);
  const finances = useFinanceRecords();
  const { t } = useTranslation();
  const canWrite = useCanWrite('finance');
  // Recompute the pending-dues set whenever the finance records change.
  const pendingIds = useMemo(() => {
    void finances;
    return pendingLoanRecordIds();
  }, [finances]);
  const [tableSearch, setTableSearch] = useState('');
  const [credentials, setCredentials] = useState<(ProvisionResult & { name: string }) | null>(null);
  const [haChecking, setHaChecking] = useState(false);
  const dr = useDateRange('all');

  // Opening the new/edit wizard and advancing steps should start at the top.
  useScrollToTop([pageMode, pageMode === 'wizard' ? currentStep : null]);

  const filteredFinances = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    return sortByEntryDateDesc(
      finances.filter((r) => {
        if (!inRange(r.createdAt, dr.range)) return false;
        if (!q) return true;
        const d = r.data;
        const hay = [
          String(d.regNo ?? ''),
          String(d.haNumber ?? ''),
          String(d.customerName ?? ''),
          String(d.mobileNumber ?? ''),
          String(d.totalAmount ?? d.amount ?? ''),
          String(d.emiAmount ?? ''),
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
    );
  }, [finances, tableSearch, dr.range]);

  const financeExportRows: ExportRow[] = useMemo(
    () =>
      filteredFinances.map((r) => {
        const d = r.data;
        return {
          Reg: String(d.regNo ?? d.haNumber ?? ''),
          Customer: String(d.customerName ?? ''),
          Mobile: String(d.mobileNumber ?? ''),
          Principal: String(d.totalAmount ?? d.amount ?? ''),
          EMI: String(d.emiAmount ?? ''),
          Months: String(d.emiPeriod ?? ''),
          Created: formatWorkspaceEntryDate(r.createdAt),
        };
      }),
    [filteredFinances],
  );

  useEffect(() => {
    const saved = loadDevJson<Record<string, unknown> | null>(FINANCE_DRAFT_KEY, null);
    if (!saved || draftRestored.current) return;
    draftRestored.current = true;
    setFormData((prev) => ({
      ...prev,
      ...saved,
      documents: prev.documents,
    } as typeof prev));
    if (
      (typeof saved.regNo === 'string' && saved.regNo.trim()) ||
      (typeof saved.customerName === 'string' && saved.customerName.trim()) ||
      (typeof saved.amount === 'string' && saved.amount.trim())
    ) {
      setDraftClientId(crypto.randomUUID());
      toast.info('Restored finance draft from this device (localStorage).');
      setPageMode('wizard');
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const { documents, ...rest } = formData;
      saveDevJson(FINANCE_DRAFT_KEY, rest);
    }, 450);
    return () => window.clearTimeout(t);
  }, [formData]);

  const openNewApplication = () => {
    if (!canWrite) return;
    setEditingFinanceId(null);
    setDraftClientId(crypto.randomUUID());
    setIsRefinance(false);
    const defaults = readFinanceDefaults();
    setFormData({
      ...emptyFinanceForm(),
      interestPercentage: defaults.interestPercentage || '',
      opInterest: defaults.opInterest || '',
    });
    setCurrentStep('vehicle');
    setPageMode('wizard');
  };

  const openMigrateApplication = () => {
    if (!canWrite) return;
    setEditingFinanceId(null);
    setDraftClientId(null);
    setIsRefinance(false);
    setPageMode('migrate');
  };

  const openRefinanceApplication = (record: FinanceRecord) => {
    if (!canWrite) return;
    setEditingFinanceId(null);
    setDraftClientId(crypto.randomUUID());
    setIsRefinance(true);
    const defaults = readFinanceDefaults();
    const base = snapshotToForm(refinanceFormFromClosed({ ...record.data, id: record.id }) as Record<string, unknown>);
    setFormData({
      ...base,
      interestPercentage: defaults.interestPercentage || '',
      opInterest: defaults.opInterest || '',
    });
    setCurrentStep('vehicle');
    setPageMode('wizard');
    toast.info('Refinance — vehicle and customer details copied. Enter new amount and EMI.');
  };

  useEffect(() => {
    const queuedId = consumeRefinanceQueue();
    if (!queuedId || !canWrite) return;
    const record = readFinanceRecords().find((r) => r.id === queuedId);
    if (record) openRefinanceApplication(record);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWrite]);

  const lookupHaFromExisting = async (haNumber: string) => {
    const trimmed = haNumber.trim();
    if (!trimmed) {
      toast.warning('Enter an HA number to check.');
      return;
    }

    const applyHaPrefill = (source: Record<string, unknown>, sourceLabel: string) => {
      const patch = withApplicantPhoto(prefillFromMainLedgerRecord(source), trimmed);
      cacheFinanceApplicantPhoto(patch);
      const filledKeys = HA_PREFILL_FIELD_KEYS.filter((key) => {
        if (key === 'isCB' || key === 'isCLR') return patch[key] === true;
        return String(patch[key] ?? '').trim() !== '';
      });
      if (filledKeys.length === 0) {
        toast.warning(
          'HA found, but no vehicle/customer details were stored on that record (only amount/EMI exist). Fill details manually.'
        );
        setFormData((prev) => ({ ...prev, haNumber: String(source.haNumber || trimmed) }));
        return;
      }
      setFormData((prev) => ({
        ...prev,
        ...patch,
        haNumber: String(source.haNumber || trimmed),
      }));
      toast.success(
        `${sourceLabel} copied vehicle & customer details (${filledKeys.length} fields). Amount / EMI were not copied — enter those for this ledger.`
      );
    };

    setHaChecking(true);
    try {
      const match = findFinanceRecordByHa(trimmed, readFinanceRecords(), editingFinanceId);
      if (match) {
        applyHaPrefill(match.data as Record<string, unknown>, 'This ledger');
        return;
      }

      const crossLedger = findTenantFinanceRecordByHa(trimmed);
      if (crossLedger) {
        applyHaPrefill(crossLedger, 'Another ledger on this device');
        return;
      }

      if (hasAuthToken()) {
        const res = await financeApi.lookupMainByHa(trimmed);
        if (res.found && res.record?.data) {
          applyHaPrefill(res.record.data as Record<string, unknown>, 'Main ledger / organization');
          return;
        }
      }

      toast.info('No record found for this HA number. Try HA-2401 … HA-2614 from seed, or create on main ledger first.');
    } catch {
      toast.error('Could not check HA number. Try again.');
    } finally {
      setHaChecking(false);
    }
  };

  const clearHaCopiedData = () => {
    const defaults = readFinanceDefaults();
    setFormData((prev) => {
      const next = { ...prev };
      for (const key of HA_PREFILL_FIELD_KEYS) {
        if (key === 'isCB' || key === 'isCLR') {
          (next as Record<string, unknown>)[key] = false;
        } else {
          (next as Record<string, unknown>)[key] = '';
        }
      }
      next.haNumber = '';
      next.interestPercentage = prev.interestPercentage || defaults.interestPercentage || '';
      next.opInterest = prev.opInterest || defaults.opInterest || '';
      return next;
    });
    removeDevKey(FINANCE_DRAFT_KEY);
    toast.info('Copied details cleared.');
  };

  const hasHaCopiedData = useMemo(() => {
    return (
      HA_PREFILL_FIELD_KEYS.some((key) => {
        const value = formData[key as keyof typeof formData];
        if (typeof value === 'boolean') return value;
        return String(value ?? '').trim() !== '';
      }) || formData.haNumber.trim() !== ''
    );
  }, [formData]);

  const backToList = () => {
    setPageMode('list');
    setEditingFinanceId(null);
    setDraftClientId(null);
    setIsRefinance(false);
    setFormData(emptyFinanceForm());
    setCurrentStep('vehicle');
  };

  const handleNext = () => {
    if (currentStep === 'vehicle') setCurrentStep('financial');
    else if (currentStep === 'financial') setCurrentStep('communications');
  };

  const handleBack = () => {
    if (currentStep === 'communications') setCurrentStep('financial');
    else if (currentStep === 'financial') setCurrentStep('vehicle');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void createFinance();
  };

  const persistLocalRecord = () => {
    const id = editingFinanceId ?? draftClientId ?? crypto.randomUUID();
    const existing = readFinanceRecords().find((r) => r.id === id);
    const now = new Date().toISOString();
    const agreementDate = adminEntryDateOnly(formData.agreementDate || formData.hpnDate);
    const entryIso = agreementDate ? adminEntryIso(agreementDate) : adminEntryIso();
    const snap = serializeFinanceForm({
      ...formData,
      agreementDate,
      startDate: agreementDate,
    } as Record<string, unknown>);
    upsertFinanceRecord({
      id,
      createdAt: existing?.createdAt ?? entryIso,
      updatedAt: now,
      source: 'local',
      data: snap,
    });
    appendDevLog(FINANCE_LOG_KEY, {
      regNo: formData.regNo,
      customerName: formData.customerName,
      amount: formData.amount,
      source: 'local',
      id,
    });
    const identity = {
      id,
      name: formData.customerName.trim(),
      phone: formData.mobileNumber.trim(),
      isNew: !editingFinanceId,
    };
    removeDevKey(FINANCE_DRAFT_KEY);
    setFormData(emptyFinanceForm());
    setEditingFinanceId(null);
    setDraftClientId(null);
    setIsRefinance(false);
    setCurrentStep('vehicle');
    return identity;
  };

  const createFinance = async () => {
    if (!canWrite) {
      toast.error('You have read-only access to Finance.');
      return;
    }
    if (!formData.documents?.length) {
      toast.info('No files attached — metadata only is saved in the browser (files are not stored).');
    }
    const minErr = validateFinanceWorkspaceMinimum(formData);
    if (minErr) {
      toast.error(minErr);
      return;
    }

    const wasEdit = !!editingFinanceId;
    try {
      const identity = persistLocalRecord();
      toast.success(
        wasEdit ? 'Finance updated and saved to the database.' : 'Finance saved to the database.'
      );
      setPageMode('list');
      window.setTimeout(() => {
        listAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);

      // Auto-provision the borrower's read-only login (email + phone password).
      if (identity.phone) {
        provisionCustomerForFinance({ name: identity.name, phone: identity.phone, recordId: identity.id })
          .then((res) => {
            if (!res) return;
            if (res.created) {
              setCredentials({ ...res, name: identity.name });
            } else if (identity.isNew) {
              toast.info(`Customer login already exists: ${res.email}`);
            }
          })
          .catch(() => {
            toast.warn('Saved, but could not create the customer login. Check your connection.');
          });
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not save — storage may be full or blocked. Try again or clear old data.');
    }
  };

  const startEdit = (r: FinanceRecord) => {
    if (!canWrite) return;
    setEditingFinanceId(r.id);
    setDraftClientId(null);
    setFormData(snapshotToForm(r.data));
    setCurrentStep('vehicle');
    setPageMode('wizard');
    toast.info('Loaded into wizard — walk through steps and submit to save changes.');
  };

  const confirmDelete = (id: string) => {
    if (!canWrite) return;
    // Guard: a borrower with pending EMIs / unsettled dues can't be deleted.
    if (isLoanRecordPending(id)) {
      toast.error("Can't delete — this customer's account still has pending EMIs / dues. Settle or close the file first.");
      return;
    }
    if (!window.confirm('Delete this finance from the workspace?')) return;
    deleteFinanceRecord(id);
    if (editingFinanceId === id) {
      setEditingFinanceId(null);
    setIsRefinance(false);
      setFormData(emptyFinanceForm());
      setCurrentStep('vehicle');
      setPageMode('list');
    }
    toast.success('Deleted.');
  };

  const currentIdx = flowSteps.findIndex((x) => x.key === currentStep);
  const wizardTitle = isRefinance ? 'Refinance vehicle finance' : editingFinanceId ? 'Edit vehicle finance' : 'New vehicle finance';
  const wizardSubtitle = isRefinance
    ? 'Same customer and vehicle — enter new principal, interest, and EMI.'
    : editingFinanceId
    ? 'Update any step and submit — the saved record on this device is replaced.'
    : 'Dedicated application flow: asset, facility, then parties. Submit returns you to the record list.';

  return (
    <div className="app-page">
      {credentials && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm">
          <div className="app-card w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3 dark:border-zinc-700">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 text-white">
                  <KeyRound className="h-4 w-4" />
                </span>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Customer login created</h3>
              </div>
              <button
                type="button"
                onClick={() => setCredentials(null)}
                className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5">
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                Share these read-only portal credentials with{' '}
                <strong>{credentials.name || 'the customer'}</strong>. They can sign in to view their EMIs, dues,
                late days and late fees.
              </p>
              {[
                { label: 'Login email', value: credentials.email },
                { label: 'Password (10-digit mobile)', value: credentials.password },
              ].map((f) => (
                <div key={f.label}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">{f.label}</p>
                  <div className="mt-1 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-600 dark:bg-zinc-800">
                    <code className="flex-1 truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{f.value}</code>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(f.value).then(
                          () => toast.success('Copied'),
                          () => toast.error('Copy failed')
                        );
                      }}
                      className="rounded-md p-1.5 text-zinc-600 hover:bg-zinc-200 dark:text-zinc-400 dark:hover:bg-zinc-700"
                      title="Copy"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/35 dark:text-amber-200">
                Tip: the customer signs in at <strong>/customer</strong> (or the “Customer login” link on the sign-in
                page).
              </p>
              <div className="flex justify-end">
                <button type="button" onClick={() => setCredentials(null)} className="app-btn-primary">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {pageMode === 'migrate' ? (
        <section className="mb-4 space-y-4 sm:mb-6" aria-label="Migrate finance">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={backToList}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {t('finance.backToList')}
            </button>
          </div>
          <MigratePanel
            kind="finance"
            canWrite={canWrite}
            cutoverDefault={
              readBooksOpening().cutoverDate || defaultAdminEntryDate() || resolveWorkspaceAsOfDate() || ''
            }
            onDone={backToList}
          />
        </section>
      ) : pageMode === 'wizard' ? (
        <section
          className="mb-4 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-900/[0.04] dark:border-zinc-700 dark:bg-zinc-900 dark:ring-zinc-700/50 sm:mb-6 sm:rounded-2xl"
          aria-label="Finance application wizard"
        >
          <div className="border-b border-slate-100 bg-gradient-to-r from-emerald-50/95 via-white to-slate-50/90 px-3 py-3 dark:border-zinc-700 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800/90 sm:px-8 sm:py-5">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={backToList}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/95 px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:bg-white dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 sm:py-2"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  {t('finance.backToList')}
                </button>
                <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700 sm:mt-4">Origination</p>
                <h1 className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 sm:mt-1 sm:text-2xl lg:text-3xl">{wizardTitle}</h1>
                <p className="mt-1 max-w-2xl text-xs leading-relaxed text-slate-600 dark:text-zinc-400 sm:mt-2 sm:text-sm">{wizardSubtitle}</p>
                {editingFinanceId ? (
                  <p className="mt-2 inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-950 ring-1 ring-amber-200/80 sm:mt-3 sm:px-2.5 sm:py-1 sm:text-xs">
                    Workspace record · {editingFinanceId.slice(0, 8)}…
                  </p>
                ) : null}
              </div>
              {mediaSrc(formData.applicantPhoto) ? (
                <img
                  src={mediaSrc(formData.applicantPhoto)}
                  alt="Applicant"
                  width={80}
                  height={80}
                  className="h-16 w-16 shrink-0 rounded-xl border-2 border-white object-cover shadow-md ring-1 ring-slate-200 sm:h-20 sm:w-20"
                />
              ) : null}
            </div>
          </div>
          <div className="space-y-3 px-2 py-3 sm:space-y-6 sm:px-8 sm:py-6">
            <div className="app-wizard mb-0 sm:mb-2" aria-label="Finance wizard progress">
              {flowSteps.map((step, i) => {
                const active = step.key === currentStep;
                const done = i < currentIdx;
                return (
                  <div
                    key={step.key}
                    className={`app-wizard-step ${active ? 'app-wizard-step-active' : ''} ${done ? 'app-wizard-step-done' : ''}`}
                  >
                    <span
                      className={`app-wizard-num ${active ? 'app-wizard-num-active' : ''} ${done ? 'app-wizard-num-done' : ''}`}
                    >
                      {done ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400">{t(step.labelKey)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {currentStep === 'vehicle' && (
              <VehicleDetails
                formData={formData}
                setFormData={setFormData}
                onNext={handleNext}
                onHaNumberCheck={lookupHaFromExisting}
                haNumberChecking={haChecking}
                onClearHaData={clearHaCopiedData}
                showClearHaData={hasHaCopiedData}
              />
            )}

            {currentStep === 'financial' && (
              <FinancialInformation
                formData={formData}
                setFormData={setFormData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}

            {currentStep === 'communications' && (
              <Communications
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                onBack={handleBack}
                customerId={customerPhotoId}
              />
            )}
          </div>
        </section>
      ) : (
        <>
          <header className="app-page-header flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">Origination</p>
              <h1 className="app-page-title mt-1">{t('finance.currentFinances')}</h1>
              <p className="app-page-sub">
                All saved facilities in one table. Open a full-page application flow to add or edit.
              </p>
            </div>
            {canWrite && (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={openMigrateApplication}
                  className="app-btn-secondary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                >
                  <BookMarked className="h-4 w-4 shrink-0 text-emerald-700" aria-hidden />
                  {t('accounts.tabMigrateFinance')}
                </button>
                <button
                  type="button"
                  onClick={openNewApplication}
                  className="app-btn-primary inline-flex w-full items-center justify-center sm:w-auto"
                >
                  {t('finance.newApplication')}
                </button>
              </div>
            )}
          </header>

          <section ref={listAnchorRef} className="app-card mb-6 overflow-hidden" aria-label="Current finances">
            <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800/90">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">{t('finance.currentFinances')}</h2>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">
                    {finances.length} record(s) · {filteredFinances.length} in {formatRangeLabel(dr.range)}
                  </p>
                </div>
                <PageSearchBar
                  id="finance-table-search"
                  value={tableSearch}
                  onChange={setTableSearch}
                  placeholder="Search reg, customer, mobile, amount…"
                  className="max-w-full sm:max-w-xs"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <DateRangeFilter
                  start={dr.range.start}
                  end={dr.range.end}
                  preset={dr.preset}
                  onStart={dr.setStart}
                  onEnd={dr.setEnd}
                  onPreset={dr.setPreset}
                  onClear={dr.clear}
                  compact
                />
                <ExportMenu
                  title={`Finance Records (${formatRangeLabel(dr.range)})`}
                  excelFilename="finance-records.xls"
                  rows={financeExportRows}
                />
              </div>
            </div>
            {finances.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-600 dark:text-zinc-400">{t('finance.noRecords')}</p>
            ) : filteredFinances.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-600 dark:text-zinc-400">{t('finance.noRows')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-slate-200 bg-white text-xs uppercase tracking-wide text-slate-500 dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-zinc-400">
                    <tr>
                      <th className="whitespace-nowrap px-4 py-2">{t('finance.regHa')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('finance.customer')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('common.amount')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('finance.emi')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('common.entryDate')}</th>
                      <th className="whitespace-nowrap px-4 py-2 text-right">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                    {filteredFinances.map((r) => {
                      const d = r.data;
                      return (
                        <tr key={r.id} className="bg-white hover:bg-slate-50/80 dark:bg-zinc-900 dark:hover:bg-zinc-800/80">
                          <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-slate-800 dark:text-zinc-200">
                            {String(d.regNo || '—')}
                            {d.haNumber ? <span className="ml-2 text-[10px] text-slate-500 dark:text-zinc-500">{String(d.haNumber)}</span> : null}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-slate-800 dark:text-zinc-200">
                            <div className="flex items-center gap-2">
                              {mediaSrc(String(d.applicantPhoto || '')) ? (
                                <img
                                  src={mediaSrc(String(d.applicantPhoto || ''))}
                                  alt=""
                                  width={32}
                                  height={32}
                                  className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
                                />
                              ) : null}
                              <span>{String(d.customerName || '—')}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 dark:text-zinc-200">{String(d.totalAmount || d.amount || '—')}</td>
                          <td className="whitespace-nowrap px-4 py-2 dark:text-zinc-200">{String(d.emiAmount || '—')}</td>
                          <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500 dark:text-zinc-400">
                            {formatWorkspaceEntryDate(r.createdAt)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => exportLoanApplicationPdf(d as Record<string, unknown>, 'finance')}
                                className="inline-flex items-center rounded border border-emerald-200 p-1.5 text-emerald-700 hover:bg-emerald-50"
                                title="View application (PDF)"
                              >
                                <FileSignature className="h-3.5 w-3.5" />
                              </button>
                              {canWrite && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(r)}
                                    className="inline-flex items-center rounded border border-slate-200 p-1.5 text-slate-700 hover:bg-white"
                                    title="Edit"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => confirmDelete(r.id)}
                                    disabled={pendingIds.has(r.id)}
                                    className="inline-flex items-center rounded border border-red-200 p-1.5 text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                    title={pendingIds.has(r.id) ? 'Account has pending EMIs / dues — settle or close before deleting' : 'Delete'}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
